import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useSessionContext } from '../contexts/SessionContext'
import { useFormData } from '../hooks/form'
import { useGeneralToast } from '../hooks/toast'
import { AGAMI_GLOBAL_EXPERIENCE_ID, SPOT_MEDIA_TYPES, SPOT_TYPES } from '../utils/constants/db.constants'
import { removeManualCodeLibrarySuffix } from '../utils/helpers/agami.helpers'
import { cleanFileName } from '../utils/helpers/upload.helpers'
import { upsertAgamiSpotV2 } from '../utils/helpers/upload-v2.helpers'
import { localizationHasMedia, useLocalizations } from '../components/LocalizationsEditor/useLocalizations'
import { getContentLanguageEndonym } from '../utils/constants/language.constants'
import { supabase } from '../utils/helpers/supabase.helpers'
import { logger } from '../utils/helpers/logger'
import { AgamiUploadFormData } from '../ViewUpload/UploadAgamiInterface'
import { AgamiSpot } from '../types/supabase'

export type AgamiFormV2Mode = 'author' | 'propose'

interface UseAgamiFormV2Args {
  /** Present when editing; absent in creation. */
  agami?: AgamiSpot
  /**
   * ONE parameter turns the propose behaviors on together (06.4's defense):
   * fixed single destination, no public visibility, no media — the proposal
   * channel (submit-agami-draft) accepts none. Three independent conditions
   * would eventually be set halfway, and a half-propose form would offer to
   * publish a visitor's proposal in Agami Global.
   */
  mode: AgamiFormV2Mode
  /** The fixed destination, required in propose mode. */
  proposeToExperienceId?: string
  onSaved: (outcome: { created: boolean; isDraft: boolean }) => void
}

/**
 * State, validation and submit of the v2 form (06.1) — the orchestration the
 * v1 keeps inline in its 400 lines. Reuses `useFormData` (one way of handling
 * forms in this repo) and keeps the v1's data shape until epic 08 changes it.
 */
export const useAgamiFormV2 = ({ agami, mode, proposeToExperienceId, onSaved }: UseAgamiFormV2Args) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const { error: toastError, success: toastSuccess } = useGeneralToast()

  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  const existingSpotEdit = useMemo(() => !!agami, [agami])

  const [searchParams] = useSearchParams()
  const addToExperience = searchParams.get('addToExperience')

  const initialFormData = useMemo<AgamiUploadFormData>(() => {
    if (existingSpotEdit && agami) {
      return {
        experiences: agami.experience_content_spots?.map((exp) => exp.experience_id) ?? [],
        spotName: agami.name,
        description: agami.description,
        latitude: agami.latitude,
        longitude: agami.longitude,
        radius: agami.radius,
        deadband: agami.deadband,
        type: agami.type,
        manual_code: removeManualCodeLibrarySuffix(agami.experience_content_spots?.[0]?.manual_code),
        content_media_type: agami.content_media_type ?? 1,
        landing_page_code_id: agami.landing_page_code_id ?? null,
        base_language: agami.base_language ?? null,
      }
    }

    // A proposal lives in ONE experience and never in the library: the draft
    // will belong to the experience's owner (D9), not to the proposer.
    if (mode === 'propose') {
      return {
        experiences: proposeToExperienceId ? [proposeToExperienceId] : [],
        spotName: '',
        description: '',
        type: 1,
        manual_code: '',
        content_media_type: 1,
        landing_page_code_id: null,
        base_language: null,
      }
    }

    const experiences = addToExperience
      ? [addToExperience, user!.defaultExperienceId]
      : [user!.defaultExperienceId]

    return {
      experiences,
      spotName: '',
      description: '',
      type: 1,
      manual_code: '',
      content_media_type: 1,
      landing_page_code_id: null,
      base_language: null,
    }
  }, [existingSpotEdit, agami, mode, proposeToExperienceId, addToExperience, user])

  const { formData, setFormData, handleDataChange, checkInputIsValid } =
    useFormData<AgamiUploadFormData>(initialFormData)

  // The additional languages (08.2): main content stays in formData/mediaFile
  // (the spot's base columns), so a monolingual agami never touches the
  // localization path at all. Proposals have no localizations. The current
  // global media type selects the bucket for their private preview URLs.
  const localizations = useLocalizations({
    agamiId: agami?.id,
    enabled: mode === 'author',
    contentMediaType: formData.content_media_type,
  })

  // Public visibility, with the v1's two conditions preserved: the option only
  // exists for users with a private library, and an agami already in Agami
  // Global starts as public. Hidden altogether in propose mode — a fixed
  // destination plus Agami Global would publish a visitor's proposal.
  const showMakePublicOption = mode === 'author' && user?.defaultExperienceId !== AGAMI_GLOBAL_EXPERIENCE_ID

  const isInGlobalExp = agami?.experience_content_spots
    ?.map((exp) => exp.experience_id)
    ?.includes(AGAMI_GLOBAL_EXPERIENCE_ID)

  const [isPublic, setIsPublic] = useState(
    mode === 'propose' ? false : !showMakePublicOption || Boolean(isInGlobalExp)
  )

  useEffect(() => {
    if (mode === 'propose') return

    if (isPublic && !formData.experiences.includes(AGAMI_GLOBAL_EXPERIENCE_ID)) {
      handleDataChange('experiences', (currExps: string[]) => [...currExps, AGAMI_GLOBAL_EXPERIENCE_ID])
    }

    if (!isPublic && formData.experiences.includes(AGAMI_GLOBAL_EXPERIENCE_ID)) {
      handleDataChange('experiences', (currExps: string[]) =>
        currExps.filter((e) => e !== AGAMI_GLOBAL_EXPERIENCE_ID)
      )
    }
  }, [isPublic, formData.experiences, mode])

  const submitPropose = useCallback(async () => {
    const { error } = await supabase.functions.invoke('submit-agami-draft', {
      body: {
        experienceId: proposeToExperienceId,
        title: formData.spotName.trim(),
        description: formData.description || undefined,
      },
    })

    if (error) {
      logger.error(error, 'AgamiFormV2.propose')
      toastError(t('uploadV2.propose.error'))
      return false
    }

    toastSuccess(t('uploadV2.propose.success'))
    return true
  }, [proposeToExperienceId, formData, toastError, toastSuccess, t])

  const submitAuthor = useCallback(
    async (isDraft: boolean) => {
      // Fields not pertinent to the type are cleared at SUBMIT, deliberately
      // diverging from the v1 (verified 2026-08-24): there, switching geo →
      // manual leaves the coordinates in the form state and writes them — a
      // manual spot with residual lat/lng in production data. The form state
      // itself keeps the values (switching back must not lose the work); only
      // what reaches the database is filtered.
      const isGeo = formData.type === SPOT_TYPES.GEO
      const isManual = formData.type === SPOT_TYPES.MANUAL

      const { error } = await upsertAgamiSpotV2({
        userId: user!.id,
        userDefaultExpId: user!.defaultExperienceId,
        isDraft,
        experiences: formData.experiences,
        ...(localizations.isDirty && { localizations: localizations.toSaveInput() }),
        media: { file: mediaFile, filename: cleanFileName({ file: mediaFile }) },
        spot: {
          id: agami?.id,
          name: formData.spotName.trim(),
          description: formData.description,
          type: formData.type,
          contentMediaType: formData.content_media_type,
          latitude: isGeo ? formData.latitude : undefined,
          longitude: isGeo ? formData.longitude : undefined,
          radius: isGeo ? formData.radius : undefined,
          deadband: isGeo ? formData.deadband : undefined,
          manualCode: isManual ? formData.manual_code : undefined,
          landingPageCodeId: isManual ? formData.landing_page_code_id : null,
          baseLanguage: formData.base_language,
          audioContentPath: agami?.audio_content_path,
        },
      })

      if (error) {
        logger.error(error, 'AgamiFormV2.save')
        toastError(t('uploadAgamiInterface.uploadError', { error: error.message }))
        return false
      }

      toastSuccess(
        existingSpotEdit ? t('uploadAgamiInterface.editSuccess') : t('uploadAgamiInterface.uploadSuccess')
      )
      // The local diff is applied: reload the rows so a second save does not
      // re-upload files or replay removals.
      if (localizations.isDirty) await localizations.reload()
      return true
    },
    [user, formData, mediaFile, agami, existingSpotEdit, localizations, toastError, toastSuccess, t]
  )

  /**
   * `isDraft` is EXPLICIT, never deduced from the submit event: the v1 reads
   * `e.nativeEvent.submitter.name`, which needs a ts-expect-error and hides the
   * intention. Here the one place that passes `isDraft: false` is readable —
   * which is also what makes "publishing is the approval" (05.5) visible in
   * code.
   */
  const submit = useCallback(
    async ({ isDraft }: { isDraft: boolean }) => {
      if (!formData.spotName.trim()) return

      // `none` is the explicit media-less state. Audio and video instead require
      // a file on the base content and on every declared localization. A
      // persisted path satisfies the rule, so ordinary edits do not require a
      // re-upload. Proposals stay outside this author-only validation.
      if (mode === 'author' && formData.content_media_type !== SPOT_MEDIA_TYPES.NONE) {
        if (!mediaFile && !agami?.audio_content_path) {
          toastError(t('uploadAgamiInterface.needMediaFile'))
          return
        }

        const localizationWithoutMedia = localizations.drafts.find((draft) => !localizationHasMedia(draft))
        if (localizationWithoutMedia) {
          toastError(
            t('uploadV2.validation.localizationMediaRequired', {
              language: getContentLanguageEndonym(localizationWithoutMedia.locale),
            })
          )
          return
        }
      }

      // Per-type validation (06.2-T7), naming the missing field instead of a
      // generic message. Geo requires a real position — the v1 lets one through
      // and writes a geolocated spot with no coordinates, malformed data in the
      // listener apps' config. Radius and deadband stay optional: NULL means
      // "use the experience defaults", which is the v1's meaning too. Custom
      // requires nothing (06.3).
      if (mode === 'author' && formData.type === SPOT_TYPES.GEO) {
        const lat = Number(formData.latitude)
        const lon = Number(formData.longitude)
        if (
          formData.latitude == null ||
          formData.longitude == null ||
          Number.isNaN(lat) ||
          Number.isNaN(lon)
        ) {
          toastError(t('uploadV2.validation.positionRequired'))
          return
        }
        if (Math.abs(lat) > 90 || Math.abs(lon) > 180) {
          toastError(t('uploadV2.validation.positionOutOfRange'))
          return
        }
      }

      if (mode === 'author' && formData.type === SPOT_TYPES.MANUAL && !formData.manual_code) {
        toastError(t('uploadV2.validation.manualCodeRequired'))
        return
      }

      setUploading(true)

      // A synchronous throw anywhere in the save (a malformed path, an invalid
      // uuid) must land as a toast, not as an unhandled rejection from a click
      // handler with the spinner stuck on.
      let saved = false
      try {
        saved = mode === 'propose' ? await submitPropose() : await submitAuthor(isDraft)
      } catch (error) {
        logger.error(error, 'AgamiFormV2.submit')
        toastError(t('uploadAgamiInterface.uploadError', { error: (error as Error).message }))
      }

      setUploading(false)

      if (saved) {
        onSaved({ created: !existingSpotEdit, isDraft })
      }
    },
    [
      formData,
      mode,
      existingSpotEdit,
      mediaFile,
      agami,
      localizations.drafts,
      submitPropose,
      submitAuthor,
      onSaved,
      toastError,
      t,
    ]
  )

  return {
    formData,
    setFormData,
    handleDataChange,
    checkInputIsValid,
    mediaFile,
    setMediaFile,
    isPublic,
    setIsPublic,
    showMakePublicOption,
    uploading,
    existingSpotEdit,
    localizations,
    submit,
  }
}
